import { z, flattenError } from "zod/v4";

const DEFAULT_REQUIRED_TEXT = "This field is required";

interface CreateZodEnumProps<T extends Record<string, string>> {
  valueObject: T;
  allowEmptyString?: boolean;
  customErrorText?: string;
}

// Creates Zod enums from string records
// Example: {PINK: "#FFAABB", BLACK: "#000000"} -> z.enum(["#FFAABB", "#000000"])
export const createZodEnums = <T extends Record<string, string>>({
  valueObject,
  allowEmptyString = false,
  customErrorText = DEFAULT_REQUIRED_TEXT,
}: CreateZodEnumProps<T>) => {
  const values = ["", ...Object.values(valueObject)] as [
    "", // Adding the empty string allows us to have empty default values
    string, // this ensures we have at least one non empty value in our array
    ...string[], // this allows for an arbitrary number of other string values
  ];

  let schema = z.enum(values, { error: customErrorText });

  if (allowEmptyString) {
    // This escape hatch allows empty string values to be valid
    return schema;
  }

  // By default we check that an empty string is not submitted during validation
  return schema.refine((val) => val !== "", customErrorText);
};

// When we know we only have string values
export const createZodStringOption = (customErrorText?: string) =>
  z.object(
    {
      label: z.string(),
      value: z.string().min(1, DEFAULT_REQUIRED_TEXT),
    },
    {
      error: customErrorText || DEFAULT_REQUIRED_TEXT,
    },
  );

// When we know we only have number values
export const createZodNumberOption = (customErrorText?: string) =>
  z.object(
    {
      label: z.string(),
      value: z.number(),
    },
    {
      error: customErrorText || DEFAULT_REQUIRED_TEXT,
    },
  );

// More flexible version that allows for both string and number values
export const createOptionSchema = (customErrorText?: string) => {
  const errorText = customErrorText || DEFAULT_REQUIRED_TEXT;
  return z
    .object({
      label: z.string(),
      value: z.string().or(z.number()),
    })
    .refine((val) => val.value !== "", errorText);
};

// Same as createOptionSchema but allows empty values (for optional autocomplete fields)
export const OptionalOptionSchema = z.object({
  label: z.string(),
  value: z.string().or(z.number()),
});

export const BoolAsString = z.preprocess(
  (val) => typeof val === "boolean" && val.toString(),
  z.enum(["true", "false"]),
);

// Reusable Zod schema for file upload metadata
export const FileUploadSchema = z.object({
  file_id: z.number().optional(),
  file: z.file().optional(),
  fileType: z.string().optional(),
  fileLabel: z.string().optional(),
  documentVisibility: z.string().optional(),
  fileName: z.string().optional(),
  active: z.boolean(),
});

// Creates the temporary form fields used by file upload sections.
// These fields are not submitted — they populate the `files` array via annotateFile.
export const createFileUploadFields = <
  TUploadTypes extends Record<string, string>,
  TVisibilityTypes extends Record<string, string>,
>({
  uploadTypes,
  visibilityTypes,
}: {
  uploadTypes: TUploadTypes;
  visibilityTypes?: TVisibilityTypes;
}) => ({
  fileType: createZodEnums({
    valueObject: uploadTypes,
    allowEmptyString: true,
  }),
  documentVisibility: visibilityTypes
    ? createZodEnums({ valueObject: visibilityTypes, allowEmptyString: true })
    : z.string(),
  fileLabel: z.string(),
  files: z.array(FileUploadSchema),
});

export const mapZodToTanStack = (err: z.ZodError<any>) => {
  // Convert Zod errors to TanStack-Form shape
  const flat = flattenError(err); // { fieldErrors, formErrors }

  const fields: Record<string, { message: string }> = {};
  Object.entries(flat.fieldErrors).forEach(([k, msgs]) => {
    if (Array.isArray(msgs) && msgs.length) {
      fields[k] = { message: msgs[0] };
    }
  });

  return {
    form: flat.formErrors[0] ?? "Please fix the highlighted errors.",
    fields,
  };
};
